// CharacterContextPopover.jsx
// -----------------------------------------------------------------------------
// Viewport-docked popover wrapper for the Character Context Hover Panel.
//
// Renders the HoverPanelMockup inside a React portal at document.body, so the
// popover is completely decoupled from any host-page layout (OpportunityCard,
// OpportunitySpreadsheet row, OpportunityDetails header — they all share this
// one positioning model). The popover docks to the right edge of the viewport
// and slides in from off-screen-right.
//
// Open/dismiss policy:
//   - Open on click of the parent CharacterContextTrigger.
//   - Close on Esc, the close button, or click outside the popover.
//   - Animation states ("opening" → "open" → "closing") drive the CSS
//     transition; the closing state lingers for the animation duration so
//     the slide-out is visible, then the popover unmounts.
//
// Spec reference: PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §2.1
// (interaction policy) and §2.3 (positioning).
// -----------------------------------------------------------------------------

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import HoverPanelMockup from './HoverPanelMockup/HoverPanelMockup';
import useCharacterContext from '../../hooks/useCharacterContext';
import IconCloseButton from './IconCloseButton';
import './CharacterContextPopover.css';

// Animation duration must match the CSS transition. Used to time the
// closing-state lifetime before unmounting. Exported so peer modules
// (notably CharacterContextTrigger) can stay in lockstep when sequencing
// "close the previous card before opening this one" handoffs — any
// retiming here updates the trigger's handoff delay automatically.
export const CLOSE_ANIM_MS = 200;


const CharacterContextPopover = ({
  open,
  onClose,
  bookUuid = undefined,
  characterId = undefined,
  characterName = undefined,
  bookTitle = undefined,
}) => {

  // Internal render-state machine:
  //   null         → not mounted at all
  //   'opening'    → mounted, off-screen (one paint, captures starting transform)
  //   'open'       → docked, fully visible
  //   'closing'    → animating out, will unmount when timer expires
  const [renderState, setRenderState] = useState(null);
  const closeTimerRef = useRef(null);
  const popoverRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Resolve the panel data via the resolver seam. Chunk 6: live fetch when
  // bookUuid + characterId are both present; mock fixture branch otherwise.
  // `bookTitle` is overlaid on the resolved envelope/fixture so the panel
  // header always reflects the live opportunity's book.
  const { characterContext, loading, error } = useCharacterContext({
    bookUuid,
    characterId,
    characterName,
    bookTitle,
  });


  // ---- State transitions driven by the `open` prop --------------------------
  useEffect(() => {
    if (open) {
      // Cancel any pending close so toggling open mid-close doesn't unmount.
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      // Mount in "opening" state for one paint, then flip to "open" so the
      // CSS transition fires from off-screen → docked.
      setRenderState('opening');
      // Double rAF guarantees the browser has committed the "opening"
      // styles before we change to "open." A single rAF is usually enough
      // but the double-rAF pattern is the bulletproof one.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setRenderState('open');
        });
      });
    } else if (renderState === 'open' || renderState === 'opening') {
      // Start the close animation. Keep the popover mounted for the duration
      // of the transition so the slide-out is visible.
      setRenderState('closing');
      closeTimerRef.current = setTimeout(() => {
        setRenderState(null);
        closeTimerRef.current = null;
      }, CLOSE_ANIM_MS);
    }
    return undefined;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup the timer if the component unmounts mid-close.
  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    },
    []
  );

  // ---- Esc-to-close ---------------------------------------------------------
  useEffect(() => {
    if (renderState !== 'open' && renderState !== 'opening') return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [renderState, onClose]);

  // ---- Click-outside-to-close -----------------------------------------------
  // Only listens once the popover is fully open — we don't want a stray
  // click on the trigger itself (which opened us) to also close us.
  useEffect(() => {
    if (renderState !== 'open') return undefined;
    const onMouseDown = (e) => {
      const pop = popoverRef.current;
      if (!pop) return;
      if (pop.contains(e.target)) return;
      // The original trigger's click is already handled by its own
      // onClick; we should NOT close in response to a click on it,
      // because then immediately re-opening on the same click would
      // race the close animation. Trigger elements carry a
      // data-aai-cc-trigger attribute to be skipped here.
      const triggerEl = e.target.closest('[data-aai-cc-trigger="true"]');
      if (triggerEl) return;
      onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [renderState, onClose]);

  // ---- Focus management ------------------------------------------------------
  // When the popover opens, move focus to the close button so keyboard users
  // can immediately Tab through the panel or hit Enter to dismiss.
  useEffect(() => {
    if (renderState === 'open' && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [renderState]);

  const handleCloseClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (renderState === null) return null;

  // Render into a portal at document.body. This decouples the popover from
  // any ancestor's z-index / overflow:hidden / transform context, which is
  // the only reliable way to overlay a docked panel above arbitrary
  // host-page chrome.
  return ReactDOM.createPortal(
    <div
      className="aai-cc-popover"
      data-state={renderState}
      role="dialog"
      aria-modal="false"
      aria-label={`Character context for ${characterName || 'character'}`}
      ref={popoverRef}
    >
      {/* The close affordance lives in the popover wrapper rather than the
       * panel itself, so the mockup component stays untouched. The shared
       * <IconCloseButton/> (see ../IconCloseButton.jsx) carries the visual
       * treatment — flat × at rest, accent-tinted pill on hover/focus,
       * focus-ring suppression — so this site only needs to layer on the
       * absolute positioning that docks it to the popover's top-right
       * corner (the chapter-popup variant uses flex flow instead). */}
      <IconCloseButton
        ref={closeButtonRef}
        onClick={handleCloseClick}
        ariaLabel="Close character context"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      />
      {characterContext ? (
        <HoverPanelMockup characterContext={characterContext} />
      ) : loading ? (
        // Loading skeleton — matches the popover's docked width so the
        // shell doesn't jump when the envelope arrives.
        <div
          className="aai-cc-popover__skeleton"
          aria-busy="true"
          aria-live="polite"
          style={{
            padding: '32px 28px',
            color: '#3a3833',
            fontFamily:
              "'Cormorant Garamond', 'Cormorant', Georgia, serif",
          }}
        >
          <p style={{ fontSize: 22, margin: '0 0 8px 0' }}>
            {characterName || 'Loading character…'}
          </p>
          <p style={{ fontSize: 14, opacity: 0.6, margin: 0 }}>
            Looking up voice context…
          </p>
        </div>
      ) : (
        // Error / empty fallback. We still render the shell so users can see
        // *something* failed and dismiss the popover. The console.warn in
        // the hook captures the underlying error for diagnostics.
        <div
          className="aai-cc-popover__error"
          role="alert"
          style={{
            padding: '32px 28px',
            color: '#3a3833',
            fontFamily:
              "'Cormorant Garamond', 'Cormorant', Georgia, serif",
          }}
        >
          <p style={{ fontSize: 22, margin: '0 0 8px 0' }}>
            {characterName || 'Character'}
          </p>
          <p style={{ fontSize: 14, opacity: 0.7, margin: 0 }}>
            {error
              ? "We couldn't load this character's voice context right now. Please try again."
              : 'No voice context is available for this character yet.'}
          </p>
        </div>
      )}
    </div>,
    document.body
  );
};

CharacterContextPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Book UUID. Required (alongside characterId) for the live-fetch branch
   *  of useCharacterContext. Without it the hook falls back to the mock
   *  fixture branch — fine for the demo route, NOT what host pages want. */
  bookUuid: PropTypes.string,
  /** Character UUID. Required (alongside bookUuid) for the live-fetch branch. */
  characterId: PropTypes.string,
  /** Display name. Renders as the panel header's loading-state fallback and
   *  is forwarded to the mock branch. */
  characterName: PropTypes.string,
  /** Book title displayed under the role/lines/chapters line in the panel
   *  header. Falsy values let the underlying fixture's bookTitle show
   *  through (if any). */
  bookTitle: PropTypes.string,
};

export default CharacterContextPopover;
